import { useState, useEffect } from "react";
import { api, apiErr, API } from "@/lib/api";
import LmsLibrary from "@/components/LmsLibrary";
import { BulletinFeed } from "@/components/BulletinBoard";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useFetch } from "@/lib/ui";
import { Empty } from "@/components/Shell";import ExamTaker from "@/components/ExamTaker";
import { PaymentWall } from "@/components/PaymentModality";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FormattedText } from "@/components/FormattedText";
import { Cross, LogOut, ClipboardList, CheckCircle2, Download, Upload, Paperclip, BookOpen, MessageSquare } from "lucide-react";

function Header({ L }) {
  const { user, logout } = useAuth();
  return (
    <header className="bg-primary text-primary-foreground">
      <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center"><Cross className="h-5 w-5 text-primary" /></div>
          <div><p className="font-heading font-extrabold text-sm">ICDH · {L("Student", "Élève")}</p><p className="text-[11px] text-primary-foreground/60">{user.name}</p></div>
        </div>
        <Button size="sm" variant="ghost" className="text-primary-foreground hover:bg-white/10" onClick={logout} data-testid="student-logout"><LogOut className="h-4 w-4 mr-1" />{L("Sign out", "Déconnexion")}</Button>
      </div>
    </header>
  );
}

export default function StudentPortal() {
  const { L } = useLang();
  const { user } = useAuth();
  const imp = !!user?._impersonated_by;
  const { data: exams, loading, reload } = useFetch(() => api.get("/exams"));
  const [active, setActive] = useState(null); // exam being taken
  const [exam, setExam] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState("exams");
  const [examType, setExamType] = useState("all");
  const EXAM_TYPE_LABELS = { official: ["Term exam (official)", "Examen de trimestre (officiel)"], quiz: ["Quiz", "Quiz"], mock: ["Mock exam", "Examen blanc"] };
  const { data: standing, reload: reloadStanding } = useFetch(() => api.get("/finance/my-standing"));
  useEffect(() => { const t = setInterval(() => reloadStanding(), 30000); return () => clearInterval(t); }, [reloadStanding]);

  const start = async (e) => {
    try { const full = await api.get(`/exams/${e.id}`); setExam(full); setActive(e); setResult(null); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const submit = async (answers) => {
    setSubmitting(true);
    try { const r = await api.post(`/exams/${active.id}/submit`, { answers }); setResult(r); setActive(null); setExam(null); reload(); toast.success(L("Exam submitted!", "Examen soumis !")); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header L={L} />
      <main className="max-w-4xl mx-auto p-4 sm:p-8">
        {(!imp && standing?.payment_locked) ? <PaymentWall standing={standing} /> : active && exam ? (
          <div className="animate-fade-up">
            <button className="text-sm text-primary mb-4" onClick={() => { setActive(null); setExam(null); }}>← {L("Back", "Retour")}</button>
            <h1 className="font-heading font-extrabold text-2xl mb-1">{exam.title}</h1>
            <p className="text-muted-foreground mb-6">{exam.subject} · {exam.questions.length} {L("questions", "questions")}</p>
            <ExamTaker exam={exam} onSubmit={submit} submitting={submitting} L={(en) => en} />
          </div>
        ) : (
          <div className="animate-fade-up">
            <div className="mb-6"><BulletinFeed limit={3} /></div>
            <div className="flex gap-2 mb-6">
              <Button variant={tab === "exams" ? "default" : "outline"} onClick={() => setTab("exams")} data-testid="student-tab-exams"><ClipboardList className="h-4 w-4 mr-1" />{L("My Exams", "Mes examens")}</Button>
              <Button variant={tab === "homework" ? "default" : "outline"} onClick={() => setTab("homework")} data-testid="student-tab-homework"><BookOpen className="h-4 w-4 mr-1" />{L("My Homework", "Mes devoirs")}</Button>
              <Button variant={tab === "library" ? "default" : "outline"} onClick={() => setTab("library")} data-testid="student-tab-library"><BookOpen className="h-4 w-4 mr-1" />{L("Library", "Bibliothèque")}</Button>
              <Button variant={tab === "director" ? "default" : "outline"} onClick={() => setTab("director")} data-testid="student-tab-director"><MessageSquare className="h-4 w-4 mr-1" />{L("Director & you", "Directeur & toi")}</Button>
            </div>
            {tab === "director" ? <DirectorContact L={L} /> : tab === "library" ? <LmsLibrary /> : tab === "homework" ? <StudentHomework L={L} /> : (
            <>
            <h1 className="font-heading font-extrabold text-2xl tracking-tight mb-1">{L("My Exams", "Mes examens")}</h1>
            <p className="text-muted-foreground mb-6">{L("Exams assigned to your class.", "Examens assignés à votre classe.")}</p>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="student-exam-type-filter">
              {[["all", ["All", "Tous"]], ...Object.entries(EXAM_TYPE_LABELS)].map(([k, v]) => (
                <Button key={k} size="sm" variant={examType === k ? "default" : "outline"} onClick={() => setExamType(k)} data-testid={`student-exam-filter-${k}`}>{L(v[0], v[1])}</Button>
              ))}
            </div>
            {result && (
              <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-5 flex items-center gap-3" data-testid="exam-result">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <div><p className="font-heading font-semibold text-emerald-800">{L("Submitted!", "Soumis !")}</p>
                  <p className="text-sm text-emerald-700">{L("Auto score", "Note auto")}: {result.auto_score}/{result.max_auto}{result.short_pending ? ` · ${L("written answers pending teacher review", "réponses écrites en attente")}` : ""}</p></div>
              </div>
            )}
            {loading ? <Empty title={L("Loading…", "Chargement…")} /> : (exams || []).length === 0 ? <Empty title={L("No exams available", "Aucun examen disponible")} /> : (
              <div className="grid sm:grid-cols-2 gap-4">
                {exams.filter((e) => examType === "all" || (e.type || "official") === examType).map((e) => (
                  <div key={e.id} className="bg-card border border-border rounded-lg p-5" data-testid={`student-exam-${e.id}`}>
                    <div className="flex justify-between items-start">
                      <div><p className="font-heading font-bold text-lg">{e.title}</p><p className="text-sm text-muted-foreground">{e.subject} · {e.question_count} {L("questions", "questions")}</p><Badge variant="outline" className="mt-1" data-testid={`student-exam-type-${e.id}`}>{L(...(EXAM_TYPE_LABELS[e.type || "official"] || [e.type, e.type]))}</Badge></div>
                      <ClipboardList className="h-5 w-5 text-primary" />
                    </div>
                    <div className="mt-4">
                      {e.attempts_allowed > 0 && (e.attempts_used || 0) > 0 && (
                        <p className="text-xs text-muted-foreground mb-2" data-testid={`attempts-used-${e.id}`}>{L("Attempts used", "Tentatives utilisées")}: {e.attempts_used}/{e.attempts_allowed}</p>
                      )}
                      {e.attempts_allowed === 0 && (e.attempts_used || 0) > 0 && (
                        <p className="text-xs text-muted-foreground mb-2" data-testid={`attempts-used-${e.id}`}>{L("Attempts used", "Tentatives utilisées")}: {e.attempts_used} ({L("unlimited", "illimité")})</p>
                      )}
                      {e.can_retake === false
                        ? <Badge variant="secondary" data-testid={`completed-${e.id}`}>{L("Completed", "Terminé")}</Badge>
                        : <Button onClick={() => start(e)} data-testid={`start-exam-${e.id}`}>{e.attempted ? L("Retake", "Repasser") : L("Start Exam", "Commencer")}</Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function DirectorContact({ L }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    const msg = message.trim();
    if (!msg) { toast.error(L("Please write a message", "Veuillez écrire un message")); return; }
    setSending(true);
    try {
      await api.post("/student/director-message", { message: msg });
      toast.success(L("Message sent to the director", "Message envoyé à la direction"));
      setMessage("");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <div className="animate-fade-up" data-testid="director-contact">
      <h1 className="font-heading font-extrabold text-2xl tracking-tight mb-3">{L("Director & you", "Directeur & toi")}</h1>
      <p className="text-muted-foreground mb-5 leading-relaxed">
        Le but de cette page est de vous faciliter la communication avec la direction en envoyant directement vos commentaires et suggestions, afin qu'elle puisse mieux vous assister. N'oubliez pas que nous sommes toujours disponibles pour vous servir.
      </p>
      <div className="bg-card border border-border rounded-lg p-5 space-y-4">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={6}
          placeholder={L("Write your message…", "Écrivez votre message…")}
          className="w-full rounded-md border border-border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="director-message-input"
        />
        <Button onClick={send} disabled={sending} data-testid="director-send-btn">
          <MessageSquare className="h-4 w-4 mr-1" />{sending ? L("Sending…", "Envoi…") : L("Send", "Envoyer")}
        </Button>
      </div>
    </div>
  );
}

function StudentHomework({ L }) {
  const { data: hw, loading } = useFetch(() => api.get("/homework"));
  const { data: subs, reload } = useFetch(() => api.get("/homework/my-submissions"));
  const subFor = (hid) => (subs || []).find((s) => s.homework_id === hid);
  const upload = async (hid, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.raw.post(`/homework/${hid}/submit`, fd); toast.success(L("Work uploaded", "Travail téléversé")); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;
  return (
    <div>
      <h1 className="font-heading font-extrabold text-2xl tracking-tight mb-1">{L("My Homework", "Mes devoirs")}</h1>
      <p className="text-muted-foreground mb-6">{L("Upload a photo of your work. Your teacher will review it.", "Téléversez une photo de votre travail. Votre enseignant le corrigera.")}</p>
      {(hw || []).length === 0 ? <Empty title={L("No homework", "Aucun devoir")} /> : (
        <div className="space-y-3">{hw.map((h) => {
          const sub = subFor(h.id);
          return (
          <div key={h.id} className="bg-card border border-border rounded-lg p-5" data-testid={`student-hw-${h.id}`}>
            <div className="flex justify-between items-start"><p className="font-heading font-bold">{h.title}</p>
              {sub && sub.status === "good" && <Badge className="bg-emerald-600 hover:bg-emerald-600">🟢 {L("Good", "Bien")}</Badge>}
              {sub && sub.status === "redo" && <Badge className="bg-red-600 hover:bg-red-600">🔴 {L("À refaire", "À refaire")}</Badge>}
              {sub && sub.status === "pending" && <Badge variant="secondary">{L("Submitted", "Remis")}</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{h.subject} · {h.class_name}</p>
            <FormattedText text={h.description} testid={`student-hw-desc-${h.id}`} />
            {h.attachment && <a href={`${API}/files/${h.attachment.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline mt-2" data-testid={`student-hw-doc-${h.id}`}><Download className="h-3 w-3" />{h.attachment.filename}</a>}
            {sub?.appreciation && <p className="text-sm italic text-muted-foreground mt-2">“{sub.appreciation}”</p>}
            <div className="flex items-center gap-3 mt-3 border-t border-border pt-3 flex-wrap">
              <label className="inline-flex items-center gap-1 text-sm text-primary cursor-pointer">
                <Upload className="h-4 w-4" />{sub ? L("Add another photo", "Ajouter une photo") : L("Upload my work", "Téléverser mon travail")}
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={(e) => upload(h.id, e.target.files?.[0])} data-testid={`student-hw-upload-${h.id}`} />
              </label>
              {(sub?.files || []).map((fl, i) => <a key={i} href={`${API}/files/${fl.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline" data-testid={`student-hw-file-${h.id}-${i}`}><Paperclip className="h-3 w-3" />{fl.filename}</a>)}
              {sub?.correction && <a href={`${API}/files/${sub.correction.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline" data-testid={`student-hw-corr-${h.id}`}><Download className="h-3 w-3" />{L("Correction", "Correction")}</a>}
            </div>
          </div>);
        })}</div>
      )}
    </div>
  );
}
